import {
  forwardRef,
  MouseEvent,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DateRange, OnSelectHandler } from 'react-day-picker';
import ReactDOM from 'react-dom';

import { Calendar } from '@/lib/shared/components/Calendar/Calendar';
import useOnClickOutside from '@/lib/shared/hooks/use-on-click-outside';
import { IClassname } from '@/lib/shared/model';
import { formatDate } from '@/lib/shared/utils/format-date';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './DateIntervalInput.module.scss';

import { ArrowIcon } from '../../icons';
import { CalendarIcon } from '../../icons/CalendarIcon';
import { SvgCross } from '../../icons/SvgCross';
import { Button, CircleButton } from '../Buttons';
import { P2, P4 } from '../Typography';

interface DateIntervalInputProps extends PropsWithChildren, IClassname {
  value: DateRange | undefined;
  onSelect: OnSelectHandler<DateRange | undefined>;
  minDays?: number;
  position: 'modal' | 'page';
}
export const DateIntervalInput = forwardRef<
  HTMLDivElement,
  DateIntervalInputProps
>(({ onSelect, value, minDays, position, children, className }, _extRef) => {
  const { t } = useTranslation('index', { keyPrefix: 'rentPage.form' });
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => {
    setIsOpen(false);
  });

  const handleOpen = (_e: MouseEvent<HTMLDivElement>) => {
    if (
      window.matchMedia('(max-width:768px)').matches ||
      position === 'modal'
    ) {
      setIsModalOpen(true);
      document.body.style.overflow = 'clip';
    } else {
      setIsOpen(true);
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setIsOpen(false);

    document.body.style.removeProperty('overflow');
  };
  return (
    <div ref={ref} className={clsx(styles.date, className)}>
      <div className={styles.date__trigger}>
        <div className={styles.date__input} onClick={handleOpen}>
          <div className={styles.date__arrival}>
            <P4 className={styles.date__label}>{t('arrival')}</P4>
            <P2 className={styles.date__value}>
              {value && value?.from ? formatDate(value.from) : t('notchosen')}
            </P2>
          </div>
          <div className={styles.date__departure}>
            <P4 className={styles.date__label}>{t('departure')}</P4>
            <P2 className={styles.date__value}>
              {value && value?.to ? formatDate(value.to) : t('notchosen')}
            </P2>
          </div>
          <CalendarIcon className={styles.date__icon} />
        </div>
        <div className={styles.date__wrapper}>
          <dialog open={isOpen} className={styles.date__dialog}>
            <div className={styles.date__dialogInputs}>
              <div className={styles.date__dialogInput}>
                <P4 className={styles.date__label}>{t('arrival')}</P4>
                <button
                  className={styles.date__reset}
                  type="button"
                  onClick={(e) => {
                    onSelect(
                      { from: undefined, to: value?.to },
                      new Date(),
                      {},
                      e
                    );
                  }}
                >
                  <SvgCross />
                </button>
                <P2 className={styles.date__value}>
                  {value && value?.from
                    ? formatDate(value.from)
                    : t('notchosen')}
                </P2>
              </div>
              <div className={styles.date__dialogInput}>
                <P4 className={styles.date__label}>{t('departure')}</P4>
                <button
                  className={styles.date__reset}
                  type="button"
                  onClick={(e) => {
                    onSelect(
                      { from: value?.from, to: undefined },
                      new Date(),
                      {},
                      e
                    );
                  }}
                >
                  <SvgCross />
                </button>
                <P2 className={styles.date__value}>
                  {value && value?.to ? formatDate(value.to) : t('notchosen')}
                </P2>
              </div>
            </div>
            <Calendar
              min={minDays}
              numberOfMonths={2}
              showOutsideDays={false}
              selected={value}
              onSelect={onSelect}
              mode="range"
            />
            <div className={styles.modal__buttons}>
              <Button
                label={t('reset')}
                className={styles.modal__buttonReset}
                as="button"
                type="button"
                size="md"
                theme="light-invert"
                onClick={(e) => onSelect(undefined, new Date(), {}, e)}
              />
              <Button
                className={styles.modal__buttonApply}
                type="button"
                label={t('apply')}
                as="button"
                size="md"
                theme="dark"
                onClick={closeModal}
              />
            </div>
          </dialog>
          <DateIntervalModal
            position={position}
            value={value}
            onSelect={onSelect}
            minDays={minDays}
            close={closeModal}
            open={isModalOpen}
          >
            {children}
          </DateIntervalModal>
        </div>
      </div>
    </div>
  );
});
DateIntervalInput.displayName = 'DateIntervalInput';

const DateIntervalModal = ({
  onSelect,
  value,
  minDays,
  close,
  open,
}: DateIntervalInputProps & { close: () => void; open: boolean }) => {
  const { t } = useTranslation('index', { keyPrefix: 'rentPage.form' });
  const ref = useRef<HTMLDialogElement>(null);
  useOnClickOutside(ref, () => close());

  const [element, setElement] = useState<HTMLElement>();

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = '';
      setElement(undefined);
    } else {
      document.body.style.overflow = 'clip';
      const elem = document.getElementById('dialog');
      if (elem) setElement(elem);
    }
  }, [open]);
  if (!element) return null;
  return (
    open &&
    ReactDOM.createPortal(
      <dialog open={open} className={styles.date__modal} ref={ref}>
        <div className={styles.modal}>
          <CircleButton
            as="button"
            type="button"
            theme="mixed"
            onClick={() => {
              close();
            }}
            name="return button"
            content={<ArrowIcon />}
            className={styles.modal__back}
          />
          <div className={styles.modal__line} />
          <div className={styles.modal__dialogInputs}>
            <div className={styles.modal__dialogInput}>
              <P4 className={styles.date__label}>{t('arrival')}</P4>
              <button
                className={styles.date__reset}
                onClick={(e) => {
                  onSelect(
                    { from: undefined, to: value?.to },
                    new Date(),
                    {},
                    e
                  );
                }}
              >
                <SvgCross />
              </button>
              <P2 className={styles.date__value}>
                {value && value?.from ? formatDate(value.from) : t('notchosen')}
              </P2>
            </div>
            <div className={styles.modal__dialogInput}>
              <P4 className={styles.date__label}>{t('departure')}</P4>
              <button
                className={styles.date__reset}
                onClick={(e) => {
                  onSelect(
                    { from: value?.from, to: undefined },
                    new Date(),
                    {},
                    e
                  );
                }}
              >
                <SvgCross />
              </button>
              <P2 className={styles.date__value}>
                {value && value?.to ? formatDate(value.to) : t('notchosen')}
              </P2>
            </div>
          </div>
          <Calendar
            min={minDays}
            numberOfMonths={2}
            showOutsideDays={false}
            selected={value}
            onSelect={onSelect}
            mode="range"
          />
          <div className={styles.modal__buttons}>
            <Button
              label={t('reset')}
              className={styles.modal__buttonReset}
              as="button"
              type="button"
              size="md"
              theme="light-invert"
              onClick={(e) => onSelect(undefined, new Date(), {}, e)}
            />
            <Button
              label={t('apply')}
              as="button"
              size="md"
              theme="dark"
              onClick={close}
              className={styles.modal__buttonApply}
            />
          </div>
        </div>
      </dialog>,
      element
    )
  );
};
